import { useState } from 'react';
import type React from 'react';

import { isValidEmail } from '../../utils';

const GENERIC_ERROR = 'Something went wrong, please try again later.';

type UseEmailSubscribeResult = {
    email: string;
    submitting: boolean;
    error: string;
    success: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
};

export function useEmailSubscribe(csrfToken: string): UseEmailSubscribeResult {
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (error) { setError(''); }
    };

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');

        const trimmed = email.trim();
        if (!trimmed) {
            setError('Please enter an email address');
            return;
        }
        if (!isValidEmail(trimmed)) {
            setError('Please enter a valid email address');
            return;
        }

        const formData = new FormData();
        formData.append('email', trimmed);
        formData.append('csrftoken', csrfToken);

        setSubmitting(true);
        try {
            const res = await fetch('/email', { method: 'POST', body: formData });
            if (res.ok) {
                setSuccess(true);
                setEmail('');
            } else {
                const data = await res.json().catch(() => ({}));
                setError(data.error || GENERIC_ERROR);
            }
        } catch {
            setError(GENERIC_ERROR);
        } finally {
            setSubmitting(false);
        }
    };

    return { email, submitting, error, success, onChange, onSubmit };
}
